import { useQuery } from '@tanstack/react-query';
import { useAuth } from './AuthProvider';

/**
 * The signed-in person's own access level per module.
 *
 * Read straight from user_module_permissions rather than through a helper per
 * module: the table's own policy already limits a non-Super-Admin to their own
 * rows, so one query returns exactly what this person is allowed to know, and
 * eight RPC round trips to answer one question about navigation would be
 * absurd.
 */

export type PermissionLevel = 'no_access' | 'member' | 'admin' | 'super_admin';

const ORDER: PermissionLevel[] = ['no_access', 'member', 'admin', 'super_admin'];

export function atLeast(level: PermissionLevel, required: PermissionLevel): boolean {
  return ORDER.indexOf(level) >= ORDER.indexOf(required);
}

export function useMyPermissions() {
  const { client, access } = useAuth();
  const userId = access.status === 'approved' ? access.profile.id : '';
  return useQuery({
    queryKey: ['my-permissions', userId],
    queryFn: async (): Promise<Record<string, PermissionLevel>> => {
      const { data, error } = await client
        .from('user_module_permissions')
        .select('module_key, access_level')
        // Filtered explicitly rather than leaning on RLS to do it.
        //
        // RLS limits an ordinary person to their own rows, so an unfiltered
        // select looked correct — and was, for seven of eight people. A SUPER
        // ADMIN can read EVERYONE's rows, so the same query returned all 64
        // and the loop below kept whichever row for a module came last. The
        // owner's Admin level silently became somebody else's no_access, and
        // the Admin tab disappeared for the one person who cannot be locked
        // out of it.
        //
        // The lesson generalises: RLS decides what you MAY read, never what
        // you MEANT to read. A query that relies on a policy to scope it
        // breaks precisely for whoever the policy scopes least.
        .eq('user_id', userId);
      if (error) {
        throw new Error(error.message);
      }
      const levels: Record<string, PermissionLevel> = {};
      for (const row of data ?? []) {
        levels[row.module_key] = row.access_level;
      }
      return levels;
    },
    enabled: access.status === 'approved',
    staleTime: 60_000,
    retry: false,
  });
}

/**
 * Whether a module should appear in navigation.
 *
 * Undecided reads as visible, not hidden. A missing row means nobody has set
 * this person's level yet — most likely a new account the seed never covered —
 * and hiding half the Hub from somebody on their first day is a worse failure
 * than showing a module they turn out not to need. The database still refuses
 * what it always refused.
 */
/**
 * The master key, client side.
 *
 * Mirrors public.is_super_admin(): a super_admin row on ANY module makes you
 * one everywhere. Without this the gates below could still hide a module from
 * the one account the database would have let through — the exact inverse of
 * a master key, and the shape of the bug the comment above describes.
 */
export function isSuperAdmin(levels: Record<string, PermissionLevel> | undefined): boolean {
  if (levels === undefined) {
    return false;
  }
  return Object.values(levels).includes('super_admin');
}

export function canSee(
  levels: Record<string, PermissionLevel> | undefined,
  moduleKey: string,
): boolean {
  if (levels === undefined) {
    return true;
  }
  if (isSuperAdmin(levels)) {
    return true;
  }
  const level = levels[moduleKey];
  return level === undefined ? true : level !== 'no_access';
}

/**
 * Whether a module's records may be edited, which is what a drag handle needs
 * to know.
 *
 * Same reasoning as canSee for an undecided level: a missing row means nobody
 * has set this person up yet, and the database refuses what it always refused,
 * so the affordance stays. What this DOES catch is an explicit no_access, where
 * offering a drag that the database will certainly refuse is just a card that
 * springs back with an error nobody can act on.
 */
export function canEditModule(
  levels: Record<string, PermissionLevel> | undefined,
  moduleKey: string,
): boolean {
  if (levels === undefined) {
    return true;
  }
  if (isSuperAdmin(levels)) {
    return true;
  }
  const level = levels[moduleKey];
  return level === undefined ? true : atLeast(level, 'member');
}
